import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { resolveImageUrl } from "@/utils/resolveImage";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  CalendarDays, Clock, MapPin, UserPlus, CheckCircle,
  Loader2, UtensilsCrossed, BookOpen, DollarSign, ShoppingCart,
  Minus, Plus, Radio, Play, Home, Bookmark, BookmarkCheck,
  ExternalLink, Share2, Heart, Download, Users,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/AuthContext";
import axios from "axios";

import API from "@/apiConfig";
const WAGC_SAVED_KEY = "wagc_my_collectives";

const DEFAULT_EVENT_TAB = "all";

const getEmbedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
};

const getMapLink = (location) => {
  if (!location) return null;
  const wordAliveAddress = "1120 W Panola Street, Carthage, TX 75633";
  if (location.includes("Word Alive") && !location.includes(","))
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(wordAliveAddress)}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
};

const formatLocation = (location) => {
  if (!location) return "Word Alive Global Center";
  // Strip Google Calendar room suffix (e.g. ", Word Alive Global Center-1-Sanctuary (120)")
  const cleaned = location.replace(/,\s*Word Alive Global Center-[^,]+$/, "");
  // If it still contains a full street address after the venue name, shorten it
  const parts = cleaned.split(",");
  if (parts.length > 2) return parts.slice(0, 2).join(",").trim();
  return cleaned.trim();
};

const decodeHtml = (text) => {
  if (!text) return "";
  const ta = document.createElement("textarea");
  ta.innerHTML = text;
  return ta.value;
};

const getSavedCollectives = () => {
  try { return JSON.parse(localStorage.getItem(WAGC_SAVED_KEY) || "[]"); }
  catch { return []; }
};

export default function LiveEventsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // --- live state ---
  const [isLive, setIsLive] = useState(false);
  const [liveVideo, setLiveVideo] = useState(null);
  const [upcomingLives, setUpcomingLives] = useState([]);
  const [liveLoading, setLiveLoading] = useState(true);

  // --- events state ---
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [month, setMonth] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);

  // --- collectives state ---
  const [allCollectives, setAllCollectives] = useState([]);
  const [savedIds, setSavedIds] = useState(getSavedCollectives);

  // --- filter tabs - sync with URL query param ---
  const VALID_TABS = ["all", "upcoming-lives", "my-collectives"];
  const getInitialTab = () => {
    const queryTab = searchParams.get("tab");
    return VALID_TABS.includes(queryTab) ? queryTab : DEFAULT_EVENT_TAB;
  };
  const [activeTab, setActiveTab] = useState(() => getInitialTab());

  // Sync tab state with URL query param
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    const next = newTab === DEFAULT_EVENT_TAB ? {} : { tab: newTab };
    setSearchParams(next, { replace: true });
  };

  // Update tab if URL changes externally (browser back/forward)
  useEffect(() => {
    const queryTab = searchParams.get("tab");
    const newTab = VALID_TABS.includes(queryTab) ? queryTab : DEFAULT_EVENT_TAB;
    if (newTab !== activeTab) setActiveTab(newTab);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- registration state ---
  const [regForm, setRegForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [regLoading, setRegLoading] = useState(false);
  const [regDone, setRegDone] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState([]);
  const [selectedFees, setSelectedFees] = useState([]);
  const [feeGuestNames, setFeeGuestNames] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);

  const { user } = useAuth();
  const navigate = useNavigate();

  // --- RSVP state ---
  const [rsvpStatus, setRsvpStatus] = useState({});
  const [rsvpLoading, setRsvpLoading] = useState(false);

  const fetchRsvpStatus = async (eventId) => {
    if (!user) return;
    try {
      const token = localStorage.getItem("wagc_session_token");
      const r = await axios.get(`${API}/events/${eventId}/rsvp-status`, { headers: { Authorization: `Bearer ${token}` } });
      setRsvpStatus(r.data);
    } catch { setRsvpStatus({}); }
  };

  const handleRsvp = async () => {
    if (!user || !selectedEvent) { toast.info("Please sign in to RSVP"); return; }
    setRsvpLoading(true);
    try {
      const token = localStorage.getItem("wagc_session_token");
      const r = await axios.post(`${API}/events/${selectedEvent.id}/rsvp`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setRsvpStatus(r.data);
      toast.success(r.data.is_going ? "You're going!" : "RSVP removed");
    } catch { toast.error("Could not update RSVP"); }
    finally { setRsvpLoading(false); }
  };

  const handleShareEvent = () => {
    if (!selectedEvent) return;
    const ev = selectedEvent;
    const deepLink = `${window.location.origin}/live-events?event=${ev.id}`;
    const displayUrl = `${window.location.origin.replace(/^https?:\/\//, "")}/live-events`;
    const isMobile = navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches;

    // Draw the text-only card (navy/gold) onto a given canvas context
    const drawTextCard = (ctx, W, H) => {
      ctx.fillStyle = "#0A1A2F"; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = "#D4AF37"; ctx.fillRect(0, 0, W, 6); ctx.fillRect(0, H - 6, W, 6);
      ctx.fillStyle = "#D4AF37"; ctx.font = "bold 28px Arial"; ctx.textAlign = "center";
      ctx.fillText("WORD ALIVE GLOBAL CENTER", W / 2, 80);
      ctx.strokeStyle = "#D4AF37"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(W / 2 - 120, 110); ctx.lineTo(W / 2 + 120, 110); ctx.stroke();
      ctx.fillStyle = "#FFFFFF"; ctx.font = "bold 52px Arial";
      const titleWords = ev.title.split(" ");
      let titleLines = []; let currentLine = "";
      for (const word of titleWords) {
        const test = currentLine ? `${currentLine} ${word}` : word;
        if (ctx.measureText(test).width > W - 160) { titleLines.push(currentLine); currentLine = word; }
        else { currentLine = test; }
      }
      if (currentLine) titleLines.push(currentLine);
      const titleStartY = 200;
      titleLines.forEach((line, i) => ctx.fillText(line, W / 2, titleStartY + i * 64));
      const dateY = titleStartY + titleLines.length * 64 + 60;
      const dateStr = new Date(ev.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
      ctx.fillStyle = "#D4AF37"; ctx.font = "bold 36px Arial";
      ctx.fillText(dateStr, W / 2, dateY);
      if (ev.time) { ctx.fillStyle = "#E8E4DA"; ctx.font = "28px Arial"; ctx.fillText(ev.time, W / 2, dateY + 48); }
      const locY = dateY + (ev.time ? 110 : 60);
      ctx.fillStyle = "#E8E4DA"; ctx.font = "26px Arial";
      ctx.fillText(formatLocation(ev.location), W / 2, locY);
      ctx.fillStyle = "#D4AF37"; ctx.font = "bold 28px Arial";
      ctx.fillText(displayUrl, W / 2, H - 64);
      const count = rsvpStatus?.attendee_count || 0;
      if (count > 0) { ctx.fillStyle = "#E8E4DA"; ctx.font = "24px Arial"; ctx.fillText(`${count} ${count === 1 ? "person" : "people"} going`, W / 2, H - 110); }
    };

    // Finalize: convert canvas to blob and share/copy
    const finalize = (canvas) => {
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        if (isMobile) {
          const file = new File([blob], `${ev.title.replace(/\s+/g, "-")}.png`, { type: "image/png" });
          if (navigator.share && navigator.canShare) {
            try {
              if (navigator.canShare({ files: [file] })) {
                await navigator.share({ title: `${ev.title} — Word Alive`, text: `Join us for ${ev.title}!\n\n${deepLink}`, files: [file] });
                return;
              }
            } catch {}
          }
          // Mobile fallback if share fails
          try { await navigator.clipboard.writeText(deepLink); toast.success("Event link copied to clipboard!"); } catch { toast.info("Link: " + deepLink); }
        } else {
          // Desktop: copy link to clipboard only
          try { await navigator.clipboard.writeText(deepLink); toast.success("Event link copied to clipboard!"); } catch { toast.info("Link: " + deepLink); }
        }
      }, "image/png");
    };

    const W = 1080, H = 1080;
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");

    // If event has an image, try loading it as Canvas background
    if (ev.image_url) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          // Draw image covering the full canvas
          const scale = Math.max(W / img.width, H / img.height);
          const dx = (W - img.width * scale) / 2;
          const dy = (H - img.height * scale) / 2;
          ctx.drawImage(img, dx, dy, img.width * scale, img.height * scale);
          // Semi-transparent dark overlay for text readability
          ctx.fillStyle = "rgba(10, 26, 47, 0.7)";
          ctx.fillRect(0, 0, W, H);
          // Gold accent bars
          ctx.fillStyle = "#D4AF37"; ctx.fillRect(0, 0, W, 6); ctx.fillRect(0, H - 6, W, 6);
          // Text overlay
          ctx.textAlign = "center";
          ctx.fillStyle = "#D4AF37"; ctx.font = "bold 28px Arial";
          ctx.fillText("WORD ALIVE GLOBAL CENTER", W / 2, 80);
          ctx.strokeStyle = "#D4AF37"; ctx.lineWidth = 2;
          ctx.beginPath(); ctx.moveTo(W / 2 - 120, 110); ctx.lineTo(W / 2 + 120, 110); ctx.stroke();
          ctx.fillStyle = "#FFFFFF"; ctx.font = "bold 52px Arial";
          const titleWords = ev.title.split(" ");
          let titleLines = []; let currentLine = "";
          for (const word of titleWords) {
            const test = currentLine ? `${currentLine} ${word}` : word;
            if (ctx.measureText(test).width > W - 160) { titleLines.push(currentLine); currentLine = word; }
            else { currentLine = test; }
          }
          if (currentLine) titleLines.push(currentLine);
          const titleStartY = 200;
          titleLines.forEach((line, i) => ctx.fillText(line, W / 2, titleStartY + i * 64));
          const dateY = titleStartY + titleLines.length * 64 + 60;
          const dateStr = new Date(ev.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
          ctx.fillStyle = "#D4AF37"; ctx.font = "bold 36px Arial";
          ctx.fillText(dateStr, W / 2, dateY);
          if (ev.time) { ctx.fillStyle = "#E8E4DA"; ctx.font = "28px Arial"; ctx.fillText(ev.time, W / 2, dateY + 48); }
          const locY = dateY + (ev.time ? 110 : 60);
          ctx.fillStyle = "#E8E4DA"; ctx.font = "26px Arial";
          ctx.fillText(formatLocation(ev.location), W / 2, locY);
          ctx.fillStyle = "#D4AF37"; ctx.font = "bold 28px Arial";
          ctx.fillText(displayUrl, W / 2, H - 64);
          const count = rsvpStatus?.attendee_count || 0;
          if (count > 0) { ctx.fillStyle = "#E8E4DA"; ctx.font = "24px Arial"; ctx.fillText(`${count} ${count === 1 ? "person" : "people"} going`, W / 2, H - 110); }
          finalize(canvas);
        } catch {
          // CORS taint or any draw error — fall back to text-only card
          drawTextCard(ctx, W, H);
          finalize(canvas);
        }
      };
      img.onerror = () => {
        // Image failed to load — fall back to text-only card
        drawTextCard(ctx, W, H);
        finalize(canvas);
      };
      img.src = resolveImageUrl(ev.image_url);
    } else {
      // No image — text-only card
      drawTextCard(ctx, W, H);
      finalize(canvas);
    }
  };

  useEffect(() => {
    axios.get(`${API}/events`).then((r) => setEvents(r.data)).catch(() => {});
    axios.get(`${API}/collectives`).then((r) => setAllCollectives(r.data)).catch(() => {});
    axios.get(`${API}/youtube/videos`)
      .then((r) => {
        const data = r.data;
        const live = data.videos?.find((v) => v.isLive);
        setIsLive(data.isLive || false);
        setLiveVideo(live || null);
        setUpcomingLives(data.upcoming_lives || []);
      })
      .catch(() => {})
      .finally(() => setLiveLoading(false));
  }, []);

  // --- Deep link: auto-open event dialog from ?event=ID ---
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);
  useEffect(() => {
    if (deepLinkHandled || events.length === 0) return;
    const eventId = searchParams.get("event");
    if (!eventId) return;
    // First check if the event is already in the loaded list
    const found = events.find((e) => e.id === eventId);
    if (found) {
      setSelectedEvent(found);
      resetSelections();
      fetchRsvpStatus(found.id);
      setDeepLinkHandled(true);
      // Scroll to registration if requested and enabled
      if (searchParams.get("register") === "true" && found.registration_enabled) {
        setTimeout(() => {
          document.querySelector('[data-testid="event-registration-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 400);
      }
    } else {
      // Event not in list (past event or not loaded) — fetch directly
      axios.get(`${API}/events/${eventId}`)
        .then((r) => {
          setSelectedEvent(r.data);
          resetSelections();
          fetchRsvpStatus(r.data.id);
          if (searchParams.get("register") === "true" && r.data.registration_enabled) {
            setTimeout(() => {
              document.querySelector('[data-testid="event-registration-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 400);
          }
        })
        .catch(() => { toast.error("Event not found"); })
        .finally(() => setDeepLinkHandled(true));
    }
  }, [events, searchParams, deepLinkHandled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle return from PayPal event payment
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    if (!paymentStatus) return;
    setTimeout(() => {
      if (paymentStatus === "success") {
        toast.success("Payment successful! You're all set. Check your email for confirmation.");
      } else if (paymentStatus === "error") {
        toast.error("Payment could not be processed. Please try registering again or contact us.");
      }
      setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete("payment"); return next; }, { replace: true });
    }, 100);
  }, []); // eslint-disable-line

  const toggleSaveCollective = (id) => {
    const next = savedIds.includes(id) ? savedIds.filter((x) => x !== id) : [...savedIds, id];
    setSavedIds(next);
    localStorage.setItem(WAGC_SAVED_KEY, JSON.stringify(next));
    toast.success(next.includes(id) ? "Collective saved!" : "Removed from My Collectives");
  };

  const myCollectives = allCollectives.filter((c) => savedIds.includes(c.id));

  // --- event helpers ---
  const resetSelections = () => {
    setRegForm({ name: "", email: "", phone: "", notes: "" });
    setRegDone(false);
    setSelectedMenu([]);
    setSelectedFees([]);
    setFeeGuestNames({});
    setSelectedProducts([]);
  };

  const eventTotal = useMemo(() => {
    if (!selectedEvent) return 0;
    let total = selectedEvent.event_fee || 0;
    const menu = selectedEvent.menu_items || [];
    selectedMenu.forEach((s) => { if (s.index >= 0 && s.index < menu.length) total += menu[s.index].price * s.qty; });
    const fees = selectedEvent.fees || [];
    selectedFees.forEach((idx) => { if (idx >= 0 && idx < fees.length) total += fees[idx].price; });
    const prods = selectedEvent.linked_product_details || [];
    selectedProducts.forEach((s) => { const p = prods.find((x) => x.id === s.product_id); if (p) total += p.price * s.qty; });
    return total;
  }, [selectedEvent, selectedMenu, selectedFees, selectedProducts]);

  const hasOptions = selectedEvent && ((selectedEvent.event_fee || 0) > 0 || (selectedEvent.menu_items || []).length > 0 || (selectedEvent.fees || []).length > 0 || (selectedEvent.linked_product_details || []).length > 0);

  const toggleMenu = (index) => setSelectedMenu((p) => { const e = p.find((s) => s.index === index); return e ? p.filter((s) => s.index !== index) : [...p, { index, qty: 1 }]; });
  const updateMenuQty = (index, qty) => { if (qty <= 0) { setSelectedMenu((p) => p.filter((s) => s.index !== index)); return; } setSelectedMenu((p) => p.map((s) => s.index === index ? { ...s, qty } : s)); };
  const toggleFee = (index) => setSelectedFees((p) => p.includes(index) ? p.filter((i) => i !== index) : [...p, index]);
  const toggleProduct = (pid) => setSelectedProducts((p) => { const e = p.find((s) => s.product_id === pid); return e ? p.filter((s) => s.product_id !== pid) : [...p, { product_id: pid, qty: 1 }]; });
  const updateProductQty = (pid, qty) => { if (qty <= 0) { setSelectedProducts((p) => p.filter((s) => s.product_id !== pid)); return; } setSelectedProducts((p) => p.map((s) => s.product_id === pid ? { ...s, qty } : s)); };

  const handleRegister = async () => {
    if (!regForm.name || !regForm.email) { toast.error("Please enter your name and email"); return; }
    const fees = selectedEvent?.fees || [];
    for (const idx of selectedFees) {
      if (fees[idx]?.collect_guest_name && !(feeGuestNames[idx] || "").trim()) {
        toast.error(`Please enter the ${fees[idx].guest_name_label || "guest name"} for "${fees[idx].name}"`);
        return;
      }
    }
    setRegLoading(true);
    try {
      if (hasOptions) {
        const feesPayload = selectedFees.map((idx) => fees[idx]?.collect_guest_name ? { index: idx, guest_name: (feeGuestNames[idx] || "").trim() } : idx);
        const body = { ...regForm, selected_menu: selectedMenu, selected_fees: feesPayload, selected_products: selectedProducts };
        const res = await axios.post(`${API}/events/${selectedEvent.id}/pay`, body);
        const data = res.data;
        if (data.approval_url) {
          toast.success("Redirecting to PayPal to complete payment...");
          window.location.href = data.approval_url;
          return;
        }
        setRegDone(true); toast.success("Successfully registered!");
      } else {
        await axios.post(`${API}/events/${selectedEvent.id}/register`, regForm);
        setRegDone(true);
        toast.success("Successfully registered!");
      }
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("You're already registered for this event.");
      } else {
        toast.error(err.response?.data?.detail || "Registration failed. Please try again.");
      }
    }
    finally { setRegLoading(false); }
  };

  const isEventPast = (eventDate) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const [y, m, d] = eventDate.split("-");
    return new Date(Number(y), Number(m) - 1, Number(d)) < today;
  };

  const fiveDaysAgo = new Date(); fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5); fiveDaysAgo.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const recentEvents = events.filter((e) => {
    const [y, m, d] = e.date.split("-");
    const evDate = new Date(Number(y), Number(m) - 1, Number(d));
    return evDate >= today || evDate >= fiveDaysAgo;
  });

  const filteredEvents = selectedDate
    ? recentEvents.filter((e) => { const [y, m, d] = e.date.split("-"); return new Date(Number(y), Number(m) - 1, Number(d)).toDateString() === selectedDate.toDateString(); })
    : recentEvents;

  const eventDates = events.map((e) => { const [y, m, d] = e.date.split("-"); return new Date(Number(y), Number(m) - 1, Number(d)); });

  // ---- Tabs config ----
  const TABS = [
    { id: "all", label: "All Events", icon: CalendarDays },
    { id: "upcoming-lives", label: "Upcoming Lives", icon: Radio },
    { id: "my-collectives", label: "My Collectives", icon: Home },
  ];

  return (
    <Layout>
      {/* ===== LIVE HERO ===== */}
      {liveLoading ? (
        <section className="bg-black" style={{ minHeight: "56vw", maxHeight: "85vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-gold" />
            <p className="text-white/40 text-sm tracking-wide uppercase">Checking broadcast status…</p>
          </div>
        </section>
      ) : isLive && liveVideo ? (
        /* --- LIVE STATE: cinematic full-bleed player --- */
        <section className="bg-black w-full" data-testid="live-hero-active">
          {/* Full-width iframe */}
          <div className="w-full relative" style={{ aspectRatio: "16/9", maxHeight: "82vh" }}>
            <iframe
              className="w-full h-full block"
              src={`https://www.youtube.com/embed/${liveVideo.id}?autoplay=1&mute=0&rel=0`}
              title={decodeHtml(liveVideo.title)}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ border: "none" }}
            />
            {/* Overlay: LIVE badge */}
            <div className="absolute top-4 left-4 z-10 pointer-events-none">
              <span className="flex items-center gap-2 bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-xl">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                Live Now
              </span>
            </div>
          </div>
          {/* Below-player strip */}
          <div className="bg-[#0d0d0d] border-t border-white/10">
            <div className="container-page py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <p className="text-white font-heading text-lg leading-tight">{decodeHtml(liveVideo.title)}</p>
                <p className="text-white/40 text-xs mt-0.5 uppercase tracking-wider">Word Alive Global Center · Streaming live</p>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <a href={`https://www.youtube.com/watch?v=${liveVideo.id}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors">
                  <ExternalLink className="h-3.5 w-3.5" /> Open on YouTube
                </a>
                <a href="https://www.facebook.com/WordAliveGlobalCenter/" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-white/50 hover:text-white text-xs transition-colors">
                  <Play className="h-3.5 w-3.5" /> Facebook
                </a>
              </div>
            </div>
          </div>
        </section>
      ) : (
        /* --- OFFLINE STATE: broadcast-station dark hero --- */
        <section className="relative text-white overflow-hidden" data-testid="live-hero-offline"
          style={{ background: "linear-gradient(135deg, #0A1A2F 0%, #0d1f3a 60%, #0A1A2F 100%)", minHeight: "380px" }}>
          {/* Subtle noise/texture overlay */}
          <div className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")", backgroundSize: "200px" }} />
          {/* Gold gradient accent - top-left */}
          <div className="absolute top-0 left-0 w-96 h-96 bg-gold/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
          {/* Gold gradient accent - bottom-right */}
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-2xl translate-x-1/4 translate-y-1/4 pointer-events-none" />

          <div className="relative z-10 container-page py-16 lg:py-20">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              {/* Left: status + CTA */}
              <div className="flex-1 text-center lg:text-left">
                {/* Off-air badge */}
                <div className="inline-flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 text-sm text-white/50 mb-6">
                  <span className="w-2 h-2 bg-gray-500 rounded-full" />
                  Not streaming right now
                </div>
                <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl mb-4 leading-none" style={{ letterSpacing: "-0.02em" }}>
                  Word Alive<br/>
                  <span style={{ color: "#D4AF37" }}>Live</span>
                </h1>
                <p className="text-white/55 text-base max-w-md mx-auto lg:mx-0 leading-relaxed mb-8">
                  Sunday services, special teachings, and revival nights — streamed live. Subscribe and never miss a broadcast.
                </p>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  <a href="https://www.youtube.com/@WordAliveGlobalCenter" target="_blank" rel="noopener noreferrer"
                    data-testid="youtube-subscribe-btn"
                    className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-full transition-all shadow-lg hover:shadow-red-900/40 hover:scale-105 text-sm">
                    <Play className="h-4 w-4 fill-current" /> Subscribe on YouTube
                  </a>
                  <a href="https://www.facebook.com/WordAliveGlobalCenter/" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white/8 hover:bg-white/15 text-white font-medium px-6 py-3 rounded-full transition-all border border-white/15 text-sm">
                    <ExternalLink className="h-4 w-4" /> Facebook Live
                  </a>
                </div>
              </div>

              {/* Right: upcoming stream card or placeholder */}
              <div className="lg:w-[380px] w-full max-w-sm shrink-0">
                {upcomingLives.length > 0 ? (
                  <div className="rounded-xl overflow-hidden border border-gold/25 shadow-2xl shadow-black/60 bg-white/5 backdrop-blur-sm" data-testid="upcoming-live-preview">
                    <div className="relative aspect-video overflow-hidden">
                      <img src={upcomingLives[0].thumbnail} alt={decodeHtml(upcomingLives[0].title)} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <div className="absolute top-3 left-3">
                        <span className="flex items-center gap-1.5 bg-amber-500/90 backdrop-blur-sm text-black text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
                          <Radio className="h-3 w-3" /> Scheduled Live
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <p className="text-white text-sm font-medium leading-snug line-clamp-2 mb-4">{decodeHtml(upcomingLives[0].title)}</p>
                      <a href={`https://www.youtube.com/watch?v=${upcomingLives[0].id}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full bg-gold text-navy text-sm font-semibold py-2.5 rounded-lg hover:bg-gold/90 transition-colors">
                        <Radio className="h-4 w-4" /> Set a Reminder
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4">
                      <Radio className="h-7 w-7 text-gold/50" />
                    </div>
                    <p className="text-white/70 font-heading text-base mb-1">No Upcoming Streams</p>
                    <p className="text-white/35 text-sm leading-relaxed">Subscribe to be notified the moment we go live.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== FILTER TABS ===== */}
      <div className="bg-white border-b border-bone-dark sticky top-0 z-20" style={{ boxShadow: "0 1px 12px rgba(0,0,0,0.07)" }}>
        <div className="container-page">
          <div className="flex gap-0.5 py-2.5 overflow-x-auto scrollbar-hide" role="tablist" data-testid="filter-tabs">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                role="tab"
                data-testid={`tab-${id}`}
                aria-selected={activeTab === id}
                onClick={() => { handleTabChange(id); setSelectedDate(null); }}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  activeTab === id
                    ? "bg-navy text-white shadow-sm"
                    : "text-gray-500 hover:text-navy hover:bg-gray-100"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
                {id === "my-collectives" && savedIds.length > 0 && (
                  <span className={`text-xs rounded-full px-1.5 leading-none py-0.5 font-bold ${activeTab === id ? "bg-gold text-navy" : "bg-navy/10 text-navy"}`}>
                    {savedIds.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ===== TAB CONTENT ===== */}

      {/* --- ALL EVENTS --- */}
      {activeTab === "all" && (
        <div className="container-page py-10" data-testid="tab-all-content">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Calendar */}
            <div className="lg:col-span-5">
              <div className="bg-white rounded-xl shadow-sm p-6 border border-bone-dark">
                <Calendar
                  data-testid="event-calendar"
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  month={month}
                  onMonthChange={setMonth}
                  modifiers={{ hasEvent: eventDates }}
                  modifiersStyles={{ hasEvent: { backgroundColor: "#D4AF37", color: "#0A1A2F", fontWeight: "700", borderRadius: "2px" } }}
                  className="w-full"
                />
                {selectedDate && (
                  <div className="mt-4 flex items-center justify-between">
                    <p className="text-sm text-gray-500">{selectedDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
                    <Button data-testid="clear-date-btn" variant="ghost" size="sm" className="text-gold" onClick={() => setSelectedDate(null)}>Show All</Button>
                  </div>
                )}
              </div>
            </div>

            {/* Event List */}
            <div className="lg:col-span-7" data-testid="events-list">
              <h2 className="font-heading text-2xl text-navy mb-4">
                {selectedDate ? `Events on ${selectedDate.toLocaleDateString("en-US", { month: "long", day: "numeric" })}` : "All Upcoming Events"}
              </h2>
              {filteredEvents.length === 0 && <p className="text-gray-500">No events found.</p>}
              <div className="flex flex-col gap-4">
                {filteredEvents.map((event) => {
                  const isPast = isEventPast(event.date);
                  return (
                    <Card
                      key={event.id}
                      className={`border-l-4 ${isPast ? "border-l-gray-300 bg-gray-50" : "border-l-gold bg-white"} hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer overflow-hidden ${isPast ? "opacity-70 grayscale-[0.2]" : ""}`}
                      onClick={() => { setSelectedEvent(event); resetSelections(); fetchRsvpStatus(event.id); setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set("event", event.id); return next; }, { replace: true }); }}
                      data-testid={`event-card-${event.id}`}
                      style={{ borderRadius: "12px", borderRight: "1px solid rgba(0,0,0,0.05)", borderTop: "1px solid rgba(0,0,0,0.05)", borderBottom: "1px solid rgba(0,0,0,0.05)" }}
                    >
                      <CardContent className="p-5 flex flex-col sm:flex-row gap-5">
                        {event.video_url && getEmbedUrl(event.video_url) ? (
                          <div className="w-full sm:w-36 h-48 sm:h-24 shrink-0 rounded-xl overflow-hidden bg-black shadow-inner" onClick={(e) => e.stopPropagation()}>
                            <iframe src={getEmbedUrl(event.video_url)} className="w-full h-full" allowFullScreen title={event.title} />
                          </div>
                        ) : event.image_url ? (
                          <div className="w-full sm:w-36 h-48 sm:h-24 shrink-0 rounded-xl overflow-hidden shadow-inner relative group-hover:scale-[1.02] transition-transform duration-500">
                            <img src={resolveImageUrl(event.image_url)} alt={event.title} className={`w-full h-full object-cover ${isPast ? "grayscale" : ""}`} />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
                          </div>
                        ) : (
                          <div className={`${isPast ? "bg-gray-400" : "bg-navy"} text-white rounded-lg px-3 py-2 text-center min-w-[56px] shrink-0 flex flex-col items-center justify-center`}>
                            <p className={`text-xs uppercase font-medium ${isPast ? "text-gray-200" : "text-gold"}`}>{new Date(event.date + "T12:00:00").toLocaleDateString("en-US", { month: "short" })}</p>
                            <p className="text-2xl font-bold font-heading leading-none mt-0.5">{new Date(event.date + "T12:00:00").getDate()}</p>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className={`font-heading text-base ${isPast ? "text-gray-500" : "text-navy"}`}>{event.title}</h3>
                            {isPast && <Badge variant="outline" className="border-gray-300 text-gray-400 rounded-full text-xs">Past</Badge>}
                            {event.registration_enabled && !isPast && (
                              <Badge className="bg-gold/15 text-gold border border-gold/30 rounded-full text-xs"><UserPlus className="h-3 w-3 mr-1" />Register</Badge>
                            )}
                          </div>
                          {event.time && <p className="text-gray-500 text-sm flex items-center gap-1 mb-1"><Clock className="h-3 w-3 text-gold" /> {event.time}</p>}
                          <a href={getMapLink(event.location)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-gray-500 hover:text-gold text-sm flex items-center gap-1 transition-colors max-w-xs">
                            <MapPin className="h-3 w-3 shrink-0 text-gold" /> <span className="truncate">{formatLocation(event.location)}</span>
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- UPCOMING LIVES --- */}
      {activeTab === "upcoming-lives" && (
        <div className="container-page py-10" data-testid="tab-upcoming-lives-content">
          <div className="mb-8">
            <h2 className="font-heading text-3xl text-navy">Upcoming Live Streams</h2>
            <p className="text-gray-500 text-sm mt-1">Scheduled broadcasts from Word Alive Global Center</p>
          </div>

          {upcomingLives.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl border border-bone-dark">
              <Radio className="h-12 w-12 text-gold/30 mx-auto mb-4" />
              <p className="font-heading text-xl text-navy mb-2">No Upcoming Streams Scheduled</p>
              <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                Subscribe to our YouTube channel to be notified the moment we go live.
              </p>
              <a
                href="https://www.youtube.com/@WordAliveGlobalCenter"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-red-600 text-white text-sm font-medium px-6 py-2.5 rounded-full hover:bg-red-700 transition-colors"
                data-testid="subscribe-youtube-btn"
              >
                <Play className="h-4 w-4 fill-current" /> Subscribe on YouTube
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingLives.map((stream) => (
                <div key={stream.id} className="bg-white border border-bone-dark rounded-xl overflow-hidden hover:shadow-lg transition-shadow" data-testid={`upcoming-stream-${stream.id}`}>
                  <div className="relative aspect-video overflow-hidden bg-black">
                    <img src={stream.thumbnail} alt={decodeHtml(stream.title)} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute top-3 left-3">
                      <span className="flex items-center gap-1.5 bg-amber-500/90 text-black text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
                        <Radio className="h-3 w-3 animate-pulse" /> Scheduled
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="font-heading text-navy text-base leading-snug line-clamp-2 mb-3">{decodeHtml(stream.title)}</p>
                    <a
                      href={`https://www.youtube.com/watch?v=${stream.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full bg-navy text-white text-sm font-medium py-2.5 rounded-lg hover:bg-navy/90 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" /> Set Reminder on YouTube
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* --- MY COLLECTIVES --- */}
      {activeTab === "my-collectives" && (
        <div className="container-page py-10" data-testid="tab-my-collectives-content">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h2 className="font-heading text-3xl text-navy">My Collectives</h2>
              <p className="text-gray-500 text-sm mt-1">Your saved home gatherings and their schedules</p>
            </div>
            <Button
              variant="outline"
              className="border-gold/40 text-navy rounded-sm shrink-0"
              onClick={() => navigate("/collectives")}
              data-testid="browse-collectives-btn"
            >
              <Home className="h-4 w-4 mr-2" /> Browse All Collectives
            </Button>
          </div>

          {myCollectives.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-sm border border-bone-dark">
              <Home className="h-12 w-12 text-gold/30 mx-auto mb-4" />
              <p className="font-heading text-xl text-navy mb-2">No Saved Collectives</p>
              <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                Save or join a Collective to see their gatherings here and stay connected with your home church community.
              </p>
              <Button
                className="btn-gold"
                onClick={() => navigate("/collectives")}
                data-testid="find-collective-btn"
              >
                Find a Collective
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myCollectives.map((c) => (
                <div key={c.id} className="bg-white border-t-4 border-gold rounded-sm shadow-sm p-6 relative" data-testid={`my-collective-${c.id}`}>
                  <button
                    className="absolute top-4 right-4 text-gold hover:text-red-400 transition-colors"
                    onClick={() => toggleSaveCollective(c.id)}
                    data-testid={`unsave-collective-${c.id}`}
                    title="Remove from My Collectives"
                  >
                    <BookmarkCheck className="h-5 w-5" />
                  </button>
                  <div className="bg-navy/10 p-2.5 rounded-sm w-fit mb-3">
                    <Home className="h-5 w-5 text-navy" />
                  </div>
                  <h3 className="font-heading text-xl text-navy mb-1 pr-8">{c.name}</h3>
                  <div className="flex items-center gap-1.5 text-gray-500 text-sm mb-1">
                    <MapPin className="h-3.5 w-3.5 text-gold shrink-0" /> {c.area}
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-500 text-sm mb-3">
                    <Clock className="h-3.5 w-3.5 text-gold shrink-0" />
                    <span className="font-medium text-navy">{c.meeting_schedule}</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">{c.description}</p>
                  <div className="mt-4 pt-3 border-t border-bone-dark">
                    <span className="text-xs text-gray-400">Host: {c.host_name}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Save more: show unsaved collectives */}
          {allCollectives.filter((c) => !savedIds.includes(c.id)).length > 0 && (
            <div className="mt-12">
              <Separator className="mb-8" />
              <h3 className="font-heading text-xl text-navy mb-4">Other Collectives Near You</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allCollectives.filter((c) => !savedIds.includes(c.id)).map((c) => (
                  <div key={c.id} className="bg-bone rounded-sm p-4 flex items-center justify-between gap-3 border border-bone-dark" data-testid={`other-collective-${c.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium text-navy text-sm truncate">{c.name}</p>
                      <p className="text-xs text-gray-500">{c.meeting_schedule}</p>
                    </div>
                    <button
                      className="text-gray-400 hover:text-gold transition-colors shrink-0"
                      onClick={() => toggleSaveCollective(c.id)}
                      data-testid={`save-collective-${c.id}`}
                      title="Save to My Collectives"
                    >
                      <Bookmark className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== EVENT DETAIL DIALOG (unchanged RSVP logic) ===== */}
      <Dialog open={!!selectedEvent} onOpenChange={() => { setSelectedEvent(null); resetSelections(); setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete("event"); next.delete("register"); return next; }, { replace: true }); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-navy">{selectedEvent?.title}</DialogTitle>
            <DialogDescription className="sr-only">Event details and registration</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedEvent?.video_url && getEmbedUrl(selectedEvent.video_url) ? (
              <div className="w-full rounded-sm overflow-hidden aspect-video bg-black">
                <iframe src={getEmbedUrl(selectedEvent.video_url)} className="w-full h-full" allowFullScreen title={selectedEvent.title} />
              </div>
            ) : selectedEvent?.image_url && (
              <img src={resolveImageUrl(selectedEvent.image_url)} alt={selectedEvent.title} className={`w-full h-56 object-cover rounded-sm ${selectedEvent && isEventPast(selectedEvent.date) ? "grayscale" : ""}`} />
            )}
            <div className="flex items-center gap-3 flex-wrap">
              <Badge className="btn-navy">
                <CalendarDays className="h-3 w-3 mr-1" />
                {selectedEvent && new Date(selectedEvent.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </Badge>
              {selectedEvent?.registration_enabled && selectedEvent && !isEventPast(selectedEvent.date) && (
                <Badge className="bg-gold text-navy rounded-sm"><UserPlus className="h-3 w-3 mr-1" /> Registration Open</Badge>
              )}
            </div>
            {selectedEvent?.time && <p className="text-gray-600 flex items-center gap-2"><Clock className="h-4 w-4 text-gold" /> {selectedEvent.time}</p>}
            <a href={getMapLink(selectedEvent?.location)} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gold flex items-center gap-2 transition-colors">
              <MapPin className="h-4 w-4 text-gold" /> {formatLocation(selectedEvent?.location)}
            </a>
            <p className="text-gray-700 leading-relaxed">{selectedEvent?.description}</p>

            {/* RSVP + Share row */}
            {selectedEvent && !isEventPast(selectedEvent.date) && (
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  data-testid="rsvp-btn"
                  variant={rsvpStatus?.is_going ? "default" : "outline"}
                  className={rsvpStatus?.is_going ? "bg-gold text-navy hover:bg-gold/90" : "border-gold text-navy hover:bg-gold/10"}
                  onClick={handleRsvp}
                  disabled={rsvpLoading}
                >
                  {rsvpLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : rsvpStatus?.is_going ? <Heart className="h-4 w-4 mr-1.5 fill-current" /> : <Users className="h-4 w-4 mr-1.5" />}
                  {rsvpStatus?.is_going ? "Going!" : "I'm Going"}
                  {(rsvpStatus?.attendee_count || 0) > 0 && <span className="ml-1.5 text-xs opacity-70">({rsvpStatus.attendee_count})</span>}
                </Button>
                <Button
                  data-testid="share-event-btn"
                  variant="outline"
                  className="border-navy/20 text-navy hover:bg-navy/5"
                  onClick={handleShareEvent}
                >
                  <Share2 className="h-4 w-4 mr-1.5" /> Share
                </Button>
              </div>
            )}

            {selectedEvent?.registration_enabled && selectedEvent && !isEventPast(selectedEvent.date) && (
              <>
                <Separator />
                {regDone ? (
                  <div className="text-center py-4" data-testid="registration-success">
                    <CheckCircle className="h-10 w-10 text-green-600 mx-auto mb-2" />
                    <p className="font-heading text-lg text-navy">You're Registered!</p>
                    <p className="text-gray-500 text-sm">We look forward to seeing you there.</p>
                  </div>
                ) : (
                  <div data-testid="event-registration-form">
                    <h4 className="font-heading text-lg text-navy mb-3 flex items-center gap-2">
                      <UserPlus className="h-5 w-5 text-gold" /> Register for this Event
                    </h4>
                    <div className="space-y-3">
                      <div><Label className="text-navy text-sm">Full Name *</Label><Input data-testid="reg-name" value={regForm.name} onChange={(e) => setRegForm((f) => ({ ...f, name: e.target.value }))} className="border-bone-dark focus:border-gold rounded-sm" /></div>
                      <div><Label className="text-navy text-sm">Email *</Label><Input data-testid="reg-email" type="email" value={regForm.email} onChange={(e) => setRegForm((f) => ({ ...f, email: e.target.value }))} className="border-bone-dark focus:border-gold rounded-sm" /></div>
                      <div><Label className="text-navy text-sm">Phone</Label><Input data-testid="reg-phone" type="tel" value={regForm.phone} onChange={(e) => setRegForm((f) => ({ ...f, phone: e.target.value }))} className="border-bone-dark focus:border-gold rounded-sm" /></div>
                      <div><Label className="text-navy text-sm">Notes</Label><Textarea data-testid="reg-notes" value={regForm.notes} onChange={(e) => setRegForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className="border-bone-dark focus:border-gold rounded-sm" placeholder="Dietary needs, accessibility, etc." /></div>

                      {hasOptions && (
                        <>
                          {(selectedEvent.event_fee || 0) > 0 && (
                            <div className="bg-gold/5 border border-gold/20 rounded-sm p-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-gold" /><span className="text-sm font-medium text-navy">Registration Fee</span></div>
                                <span className="font-semibold text-navy">${selectedEvent.event_fee.toFixed(2)}</span>
                              </div>
                            </div>
                          )}
                          {(selectedEvent.menu_items || []).length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-2"><UtensilsCrossed className="h-4 w-4 text-gold" /><Label className="text-navy font-semibold text-sm">Food Menu</Label></div>
                              <div className="space-y-2">
                                {selectedEvent.menu_items.map((item, idx) => {
                                  const sel = selectedMenu.find((s) => s.index === idx);
                                  return (
                                    <div key={idx} className={`flex items-center justify-between p-2.5 rounded-sm border cursor-pointer ${sel ? "bg-gold/10 border-gold/30" : "border-bone-dark"}`} onClick={() => toggleMenu(idx)}>
                                      <div className="flex items-center gap-2">
                                        <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center ${sel ? "bg-gold border-gold" : "border-gray-300"}`}>{sel && <CheckCircle className="h-3 w-3 text-white" />}</div>
                                        <span className="text-sm">{item.name}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">${item.price.toFixed(2)}</span>
                                        {sel && <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}><button className="p-0.5 rounded hover:bg-gold/20" onClick={() => updateMenuQty(idx, sel.qty - 1)}><Minus className="h-3 w-3" /></button><span className="text-xs w-5 text-center font-medium">{sel.qty}</span><button className="p-0.5 rounded hover:bg-gold/20" onClick={() => updateMenuQty(idx, sel.qty + 1)}><Plus className="h-3 w-3" /></button></div>}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {(selectedEvent.fees || []).length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-2"><BookOpen className="h-4 w-4 text-gold" /><Label className="text-navy font-semibold text-sm">Additional Options</Label></div>
                              <div className="space-y-2">
                                {selectedEvent.fees.map((fee, idx) => {
                                  const isSelected = selectedFees.includes(idx);
                                  return (
                                    <div key={idx}>
                                      <div className={`flex items-center justify-between p-2.5 rounded-sm border cursor-pointer ${isSelected ? "bg-gold/10 border-gold/30" : "border-bone-dark"}`} onClick={() => toggleFee(idx)} data-testid={`fee-option-${idx}`}>
                                        <div className="flex items-center gap-2">
                                          <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center ${isSelected ? "bg-gold border-gold" : "border-gray-300"}`}>{isSelected && <CheckCircle className="h-3 w-3 text-white" />}</div>
                                          <div><span className="text-sm">{fee.name}</span>{fee.description && <p className="text-xs text-gray-400">{fee.description}</p>}</div>
                                        </div>
                                        <span className="text-sm font-medium">${fee.price.toFixed(2)}</span>
                                      </div>
                                      {isSelected && fee.collect_guest_name && (
                                        <Input
                                          data-testid={`fee-guest-input-${idx}`}
                                          placeholder={fee.guest_name_label || "Guest's Name"}
                                          value={feeGuestNames[idx] || ""}
                                          onChange={(e) => setFeeGuestNames((p) => ({ ...p, [idx]: e.target.value }))}
                                          onClick={(e) => e.stopPropagation()}
                                          className="mt-1.5 text-sm"
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {(selectedEvent.linked_product_details || []).length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-2"><ShoppingCart className="h-4 w-4 text-gold" /><Label className="text-navy font-semibold text-sm">Available Products</Label></div>
                              <div className="space-y-2">
                                {selectedEvent.linked_product_details.map((p) => {
                                  const sel = selectedProducts.find((s) => s.product_id === p.id);
                                  return (
                                    <div key={p.id} className={`flex items-center justify-between p-2.5 rounded-sm border cursor-pointer ${sel ? "bg-gold/10 border-gold/30" : "border-bone-dark"} ${!p.in_stock ? "opacity-50 pointer-events-none" : ""}`} onClick={() => p.in_stock && toggleProduct(p.id)}>
                                      <div className="flex items-center gap-2">
                                        <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center ${sel ? "bg-gold border-gold" : "border-gray-300"}`}>{sel && <CheckCircle className="h-3 w-3 text-white" />}</div>
                                        {p.image_url && <img src={resolveImageUrl(p.image_url)} alt="" className="w-8 h-8 rounded-sm object-cover" />}
                                        <span className="text-sm">{p.name}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">${p.price.toFixed(2)}</span>
                                        {sel && <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}><button className="p-0.5 rounded hover:bg-gold/20" onClick={() => updateProductQty(p.id, sel.qty - 1)}><Minus className="h-3 w-3" /></button><span className="text-xs w-5 text-center font-medium">{sel.qty}</span><button className="p-0.5 rounded hover:bg-gold/20" onClick={() => updateProductQty(p.id, sel.qty + 1)}><Plus className="h-3 w-3" /></button></div>}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          <div className="bg-navy/5 border border-navy/10 rounded-sm p-3" data-testid="event-total">
                            <div className="flex items-center justify-between">
                              <span className="font-heading text-navy">Total</span>
                              <span className="font-heading text-xl text-navy">${eventTotal.toFixed(2)}</span>
                            </div>
                          </div>
                        </>
                      )}

                      <Button
                        data-testid="reg-submit-btn"
                        className="w-full btn-gold uppercase tracking-wider font-medium"
                        onClick={handleRegister}
                        disabled={regLoading}
                      >
                        {regLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                        {regLoading ? "Registering..." : eventTotal > 0 ? `Register & Checkout ($${eventTotal.toFixed(2)})` : "Register Now"}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}

            {selectedEvent && isEventPast(selectedEvent.date) && selectedEvent.registration_enabled && (
              <>
                <Separator />
                <div className="text-center py-4 bg-gray-50 rounded-sm">
                  <p className="text-gray-600 text-sm">Registration for this event has closed.</p>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
